import decomposeSyllable from '../helpers/decomposeSyllable';
import { decodeUTF8, encodeUTF8 } from '../helpers/utf8';
import { stickLeft, stickRight } from '../constants';
import { RenderInput } from '../types/RenderOptions';
import prepareWords, { RenderWord } from './prepareWords';
import connectorArc from './connectorArc';
import wordMetrics from './wordMetrics';

/** Imperative renderer. Call after mount and font loading, never during React render. */
const renderTextImage = (props: RenderInput): HTMLCanvasElement => {
    const ONSET = 0;
    const RHYME = 1;
    const ONSET_COLOR = ['#000000', '#336699', '#CC7733', '#338844', '#442222', '#222266', '#222222'];
    const RHYME_COLOR = ['#333333', '#4499BB', '#EEAA44', '#66AA44', '#884444', '#6666AA', '#777777'];
    const COLOR = [ONSET_COLOR, RHYME_COLOR];
    const getToneColor = (tone: number, part: number) => {
        if (tone >= 0 && tone <= 6 && (part === ONSET || part === RHYME)) return COLOR[part][tone];
        return '#000000';
    };

    const LEVEL = 0;
    const RISING = -Math.PI / 9;
    const FALLING = Math.PI / 15;
    const ROTATE_TRANSFORM = [LEVEL, LEVEL, RISING, LEVEL, FALLING, RISING, LEVEL];
    const HEIGHT_TRANSFORM = [0.5, 0.3, 0.4, 0.5, 0.7, 0.7, 0.7];

    const GLOSS_HEIGHT_RATIO = props.config.honziHeightRatio * 0.25;
    const HONZI_HEIGHT_RATIO = props.config.honziHeightRatio * 0.50;
    const WRAP_SIZE_CURRENT = props.config.wrapSize;
    const LINE_HEIGHT_MULTIPLIER = props.config.lineHeightMultiplier;
    const HONZI_MODE = props.config.fontType;
    const GREYSCALE = props.config.greyscale;
    const NOPRONMARK = '#';
    const SHOW_LINE_NUM = props.config.showLineNum;
    const SHOW_LINE_BREAK = props.config.showLineBreak;
    const SHOW_SEG_LINE = props.config.showSegLine;

    const { style } = props;
    const pre = document.createElement('pre');

    const _standard = `line-height: ${style.lineHeight}; text-align: ${style.align}; padding: 0; display: block; position: fixed; top: 100%; overflow: hidden;`;
    const style0 = `${style.honziSize*(HONZI_MODE === 'Normal'? 1: 1.75)}pt "AR PL UKai HK", "Kaiti TC"`;
    const style1 = `${style.size}pt ${style.font}`;
    const style2 = `italic ${Math.round(style.size * 0.55)}pt "FiraI"`;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas rendering is not available in this browser.');

    const strip = prepareWords(props.words, props.config.stickyPunctuation);
    const MAX_GLOSS_LANG = Math.max(props.config.langOrder.split(',').filter(Boolean).length,
        ...strip.map((word) => word.gloss && word.gloss !== '#' ? word.gloss.split('#').length : 0));

    const sideMargin = style.sideMargin * 1.0;
    const lineMargin = style.lineMargin * 1.0 * LINE_HEIGHT_MULTIPLIER;
    const wordSpacing = style.wordSpacing * 1.0;
    const { displayTones } = style;
    const { lineHeight } = style;
    const { withHonzi } = style;
    const { withGloss } = style;
    const honziSize = style.honziSize;
    const { arcMargin } = style;
    const dancing = style.mode === 'dancing';

    document.body.appendChild(pre);
    try {

        pre.innerText = 'Naang'; // Longest possible Jyutping string, and a word with both tall and tailing letters.
        pre.setAttribute('style', _standard);
        pre.style.font = style1;

        const x = pre.offsetWidth;
        const y = pre.offsetHeight; // Line height before adjustment
        const base = y * lineHeight;
        const rowHeight = y * 3;
        const { system } = style;

        const getJPWidth = (jp: string) => {
            pre.innerText = jp;
            pre.style.font = style1;
            const syllWidth = pre.offsetWidth;
            const theta = (dancing ? ROTATE_TRANSFORM[Number(decomposeSyllable(jp, system).tone)] : 0);
            return syllWidth * Math.abs(Math.cos(theta)) + y * Math.abs(Math.sin(theta));
        };

        const getCharWidth = (char: string) => {
            pre.innerText = char;
            pre.style.font = style0;
            return pre.offsetWidth;
        };

        const getAlignedChars = (text: string, syllableCount: number) => {
            let chars = decodeUTF8(encodeUTF8(text));
            while (stickRight.includes(chars[0]) && chars.length > 1) {
                chars = [chars[0] + chars[1], ...chars.slice(2)];
            }
            if (chars.length > syllableCount) {
                chars[syllableCount - 1] = chars.slice(syllableCount - 1).join('');
            }
            return chars;
        };
        const getWordMetrics = (word: RenderWord) => {
            const sylls = word[system].split('-');
            const chars = getAlignedChars(word.char, sylls.length);
            const glossWidths = withGloss && word.gloss && word.gloss !== '#'
                ? word.gloss.split('#').map((gloss) => {
                    pre.innerText = gloss;
                    pre.style.font = style2;
                    return pre.offsetWidth;
                }) : [];
            return wordMetrics(sylls.map(getJPWidth),
                sylls.map((_, index) => withHonzi ? getCharWidth(chars[index] || '') : 0),
                glossWidths, wordSpacing);
        };
        const getRequiredWidth = (word: RenderWord) => getWordMetrics(word).width;

        const widths = strip.map(getRequiredWidth);
        const maxWidth = props.config.customBreak
            ? widths.reduce((sum, width) => sum + width, 0) + wordSpacing
            : Math.max(x * WRAP_SIZE_CURRENT, ...widths, wordSpacing);
        canvas.width = maxWidth + sideMargin * 2;

        const getStripNetHeight = (h: number, withHonzi: boolean, withGloss: boolean, dancing: boolean) => h * ((dancing ? 1 : 0.5) + (withHonzi ? HONZI_HEIGHT_RATIO : 0) + (withGloss ? MAX_GLOSS_LANG * GLOSS_HEIGHT_RATIO : 0));
        const getStripFullHeight = (h: number, withHonzi: boolean, withGloss: boolean, dancing: boolean) => getStripNetHeight(h, withHonzi, withGloss, dancing) + lineMargin;
        // let stripNetHeight = getStripNetHeight(rowHeight, withHonzi, withGloss);
        const stripFullHeight = getStripFullHeight(rowHeight, withHonzi, withGloss, dancing);
        canvas.height = 1 * stripFullHeight - lineMargin + (props.config.watermark ? 0.2 * rowHeight : 0);
        // Assuming there's only one line per strip first.
        context.fillStyle = style.background;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fill();

        let stripIndex = 0;
        let stripBeginY = 0;
        let stripMainBeginY = 0;
        let stripGlossBeginY = 0;
        let stripEndY = 0;

        const increaseCanvasHeight = (lineCount: number) => {
            const tmpCanvas = document.createElement('canvas');
            tmpCanvas.width = canvas.width;
            tmpCanvas.height = canvas.height;
            tmpCanvas.getContext('2d')?.drawImage(canvas, 0, 0);
            canvas.height = (lineCount) * stripFullHeight - lineMargin + (props.config.watermark ? 0.2 * rowHeight : 0);
        canvas.getContext('2d')?.drawImage(tmpCanvas, 0, 0);
        };

        const moveToNewLine = (lineCount: number) => {
            stripIndex = lineCount - 1;
            stripBeginY = stripIndex * stripFullHeight;
            stripMainBeginY = stripBeginY + (withHonzi ? HONZI_HEIGHT_RATIO : 0) * rowHeight;
            stripGlossBeginY = stripMainBeginY + rowHeight * (dancing ? 1 : 0.5);
            stripEndY = stripGlossBeginY + (withGloss ? MAX_GLOSS_LANG * GLOSS_HEIGHT_RATIO * rowHeight : 0);
        };

        const drawStripBackground = () => {
        // Draw new strip
            context.fillStyle = '#FFFFFF';
            context.beginPath();
            context.fillRect(0, Math.floor(stripMainBeginY), canvas.width - sideMargin * 2, Math.ceil(rowHeight / 2));
            context.fill();

            if (dancing) {
                context.fillStyle = '#F0F0F0';
            }
            context.beginPath();
            context.fillRect(0, Math.floor(stripMainBeginY + rowHeight / 2), canvas.width - sideMargin * 2, Math.ceil(rowHeight / 2));
            context.fill();

            if (withHonzi) {
                context.fillStyle = '#FFFFFF';
                context.beginPath();
                context.fillRect(0, Math.floor(stripBeginY), canvas.width - sideMargin * 2, Math.ceil(HONZI_HEIGHT_RATIO * rowHeight));
                context.fill();
            }

            if (withGloss) {
                context.fillStyle = '#FFFFFF';
                context.beginPath();
                context.fillRect(0, Math.floor(stripGlossBeginY), canvas.width - sideMargin * 2, Math.ceil(MAX_GLOSS_LANG * GLOSS_HEIGHT_RATIO * rowHeight));
                context.fill();
            }

            // Add watermark
            if (props.config.watermark) {
                context.font = `${honziSize * 0.4}pt FiraI`;
                context.fillStyle = '#999999';
                context.fillText('Generated by hambaanglaang.hk', 0, stripEndY + 0.15 * rowHeight);
            }

            if (SHOW_LINE_BREAK) {
                context.lineWidth = style.size / 4; // this.style.stroke;
                context.strokeStyle = '#CCAAAA';
                context.beginPath();
                context.moveTo(0, stripEndY);
                context.lineTo(canvas.width - sideMargin * 2, stripEndY);
                context.stroke();
            }
        };

        let usedWidth = 0;
        let lineCount = 0;

        strip.forEach((word, i) => {
            let endClause = false;
            const wordWidth = getRequiredWidth(word);
            if (lineCount === 0 ||
                (!props.config.customBreak && (wordWidth + usedWidth) > (maxWidth - 0.5 * wordSpacing)  && usedWidth > 0)) {
                context.restore();
                increaseCanvasHeight(++lineCount);
                context.save();
                context.translate(sideMargin, 0);
                moveToNewLine(lineCount);
                drawStripBackground();
                usedWidth = 0;

                // Draw Word Segmentation Line for the first word
                if (SHOW_SEG_LINE) {
                    context.lineWidth = style.size / 70; // this.style.stroke;
                    context.strokeStyle = '#AAAAAA';
                    context.beginPath();
                    context.moveTo(usedWidth + 0.5 * wordSpacing, stripBeginY);
                    context.lineTo(usedWidth + 0.5 * wordSpacing, stripEndY);
                    context.stroke();
                }

                if (SHOW_LINE_NUM) {
                    context.font = style1;
                    context.fillStyle = '#333333';
                    context.fillText(String(lineCount), 0, stripBeginY + wordSpacing);
                }
            }

            const sylls = word[system].split('-');
            const chars = getAlignedChars(word.char, sylls.length);
            const wordBeginX = usedWidth;

            // Draw Gloss (word level)
            if (withGloss) {
                if (word.gloss && word.gloss !== '#') {
                // Add multilingual support
                // Different langugaes delimited by #
                    const glosses = word.gloss.split('#');
                    context.font = style2;
                    context.fillStyle = '#333333';
                    pre.style.font = context.font;

                    for (let g = 0, prevHeightSum = 0; g < glosses.length; ++g) {
                        pre.innerText = glosses[g];
                        const posX = usedWidth + (wordWidth - pre.offsetWidth + wordSpacing) / 2;
                        const posY = stripGlossBeginY + 0.3 * base + prevHeightSum;
                        prevHeightSum += Math.max(rowHeight * GLOSS_HEIGHT_RATIO, pre.offsetHeight);
                        context.fillText(glosses[g], posX, posY);
                    }
                }
            }

            // Centre the aligned cells within any extra width reserved for glosses.
            usedWidth += getWordMetrics(word).inset;
            // Draw the components (Jyutping, Honzi) by syllable

            for (let i = 0, prev = null, properNamePoint = null; i < sylls.length; ++i) {
                const syll = decomposeSyllable(sylls[i], system);
                const toneRow = (dancing ? HEIGHT_TRANSFORM[Number(syll.tone)] : 0.3);
                const contour = (dancing ? ROTATE_TRANSFORM[Number(syll.tone)] : 0);
                const charFullWidth = (withHonzi ? getCharWidth(chars[i] || '') : 0);
                const syllWidth = getJPWidth(syll.syll);
                const onsetWidth = getJPWidth(syll.syll) - getJPWidth(syll.syll.substr(syll.onset.length));

                const normalX = usedWidth + wordSpacing;
                const normalY = toneRow * rowHeight + stripMainBeginY;

                // Adjust normalX for rising tones

                // Draw Jyutping / Yale

                context.font = `${style1}`;
                context.textAlign = style.align;
                context.lineWidth = style.stroke;
                context.strokeStyle = style.strokeColor;
                context.fillStyle = style.color;
                context.save();

                let horizontalAdjust = (dancing && ROTATE_TRANSFORM[Number(syll.tone)] < 0
                    ? Math.abs(y * Math.sin(ROTATE_TRANSFORM[Number(syll.tone)])) : 0);
                if (charFullWidth > syllWidth + horizontalAdjust) {
                    horizontalAdjust += (charFullWidth - syllWidth - horizontalAdjust) / 2;
                }
                context.translate(normalX + horizontalAdjust, normalY);
                context.rotate(contour);
                context.textAlign = 'left';

                const gradient = context.createLinearGradient(0, 0, syllWidth, 0);
                const o2s_ratio = onsetWidth / syllWidth;
                const color_onset = getToneColor(GREYSCALE ? 0 : Number(syll.tone), ONSET);
                const color_rhyme = getToneColor(GREYSCALE ? 0 : Number(syll.tone), RHYME);

                gradient.addColorStop(0, color_onset);
                if (o2s_ratio >= 0 && o2s_ratio <= 1) {
                    gradient.addColorStop(o2s_ratio, color_onset);
                    gradient.addColorStop(o2s_ratio, color_rhyme);
                }
                gradient.addColorStop(1, color_rhyme);
                context.fillStyle = gradient;

                const word_for_display = (displayTones ? syll.syll : syll.notone).replace(NOPRONMARK, '');

                if (style.stroke) {
                    context.strokeText(word_for_display, 0, 0);
                }
                context.fillText(word_for_display, 0, 0);
                context.restore();

                if (prev) {
                // Draw Connector Lines
                    context.lineWidth = style.size / 50; // this.style.stroke;
                    context.strokeStyle = 'red';

                    const twopoints = {
                        start: prev,
                        end: { x: normalX + horizontalAdjust, y: normalY + y * 0.1 },
                        end_high: { x: normalX + horizontalAdjust, y: normalY - y * 0.5 },
                        heightAdjustment: toneRow,
                    };

                    // ARC
                    if (dancing) {
                        const highConnect = twopoints.heightAdjustment > 0.45;
                        const arc = connectorArc(twopoints.start,
                            highConnect ? twopoints.end_high : twopoints.end,
                            rowHeight / 3, highConnect, arcMargin);
                        if (arc) {
                            context.beginPath();
                            context.arc(arc.center.x, arc.center.y, arc.radius, arc.startAngle, arc.endAngle);
                            context.stroke();
                        }
                    }
                }

                prev = { x: normalX + syllWidth * Math.cos(contour), y: normalY + y * Math.sin(contour) };

                // Drawing Honzi
                if (withHonzi) {
                    if (chars[i]) {
                        context.font = style0;
                        const stickLeftString = chars[i].match(new RegExp(`^[${stickRight.join('')}]*`));
                        const stickRightString = chars[i].match(new RegExp(`[${stickLeft.join('')}]*$`));

                        const stickLeftWidth = getCharWidth(stickLeftString ? stickLeftString[0] : '');
                        const stickRightWidth = getCharWidth(stickRightString ? stickRightString[0] : '');
                        if (stickRightWidth > 0) endClause = true;

                        const posX = normalX + (charFullWidth > syllWidth + horizontalAdjust ? 0 : (syllWidth - charFullWidth) / 2);
                        const posY = stripMainBeginY - 0.3 * base;

                        context.fillStyle = '#333333';
                        context.fillText(chars[i], posX, posY);

                        // Handle proper names
                        if (word.directive === '()') {
                            if (i === 0) {
                                properNamePoint = {
                                    x: posX + stickLeftWidth,
                                    y: stripMainBeginY - 0.2 * base,
                                };
                            }
                            if (i === sylls.length - 1) {
                                if (properNamePoint) {
                                    context.lineWidth = style.size / 15;
                                    context.strokeStyle = 'black';
                                    const twopoints = {
                                        start: properNamePoint,
                                        end: { x: posX + charFullWidth - stickRightWidth, y: properNamePoint.y },
                                    };
                                    context.beginPath();
                                    context.moveTo(twopoints.start.x, twopoints.start.y);
                                    context.lineTo(twopoints.end.x, twopoints.end.y);
                                    context.stroke();
                                }
                            }
                        }
                    }
                }
                usedWidth += Math.max(syllWidth, charFullWidth) + wordSpacing;
            }
            usedWidth = wordBeginX + wordWidth;
            // Draw Word Segmentation Line
            if (SHOW_SEG_LINE) {
                context.lineWidth = style.size / 70 * (endClause? 2 : 1); // this.style.stroke;
                context.strokeStyle = '#AAAAAA';
                context.beginPath();
                context.moveTo(usedWidth + 0.5 * wordSpacing, stripBeginY);
                context.lineTo(usedWidth + 0.5 * wordSpacing, stripEndY);
                if (endClause){
                    context.moveTo(usedWidth + 0.6 * wordSpacing, stripBeginY);
                    context.lineTo(usedWidth + 0.6 * wordSpacing, stripEndY);
                }
                context.stroke();
            }
        });

        context.restore();
        return canvas;
    } finally {
        pre.remove();
    }
};

export default renderTextImage;
